import { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { X, ArrowLeft, ArrowRight, Check } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const OnboardingTour = ({ role, onComplete }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [isVisible, setIsVisible] = useState(false);

  // Check if user has completed tour - from both localStorage AND backend
  useEffect(() => {
    const checkTourStatus = async () => {
      // First check localStorage for immediate response
      const localTourCompleted = localStorage.getItem(`tour_completed_${role}`);
      if (localTourCompleted === 'true') {
        setIsVisible(false);
        return;
      }

      // Then check backend for persistent state
      try {
        const response = await axios.get(`${API}/auth/user/onboarding-status`);
        const onboardingCompleted = response.data?.onboarding_completed;
        
        if (onboardingCompleted) {
          // Sync localStorage with backend
          localStorage.setItem(`tour_completed_${role}`, 'true');
          setIsVisible(false);
        } else {
          setIsVisible(true);
        }
      } catch (error) {
        // If API fails, fall back to showing tour only if localStorage doesn't have it
        if (!localTourCompleted) {
          setIsVisible(true);
        }
      }
    };

    checkTourStatus();
  }, [role]);

  const adminSteps = [
    {
      target: '[data-tour="overview"]',
      title: 'Welcome to MyMedMember! 🎉',
      content: 'This is your dashboard overview. Here you can see key metrics about your clinic at a glance.',
      position: 'bottom'
    },
    {
      target: '[data-tour="members"]',
      title: 'Manage Members',
      content: 'Click here to view, add, edit, and manage all your clinic members. You can assign memberships, add notes, and track appointments.',
      position: 'right'
    },
    {
      target: '[data-tour="plans"]',
      title: 'Membership Plans',
      content: 'Create and manage your membership plans. Set pricing, duration, and benefits for different tiers.',
      position: 'right'
    },
    {
      target: '[data-tour="appointments"]',
      title: 'Appointments',
      content: 'Schedule and manage patient appointments. Track upcoming visits and appointment history.',
      position: 'right'
    },
    {
      target: '[data-tour="payments"]',
      title: 'Payment Tracking',
      content: 'Record and monitor all payment transactions. View payment history and outstanding balances.',
      position: 'right'
    },
    {
      target: '[data-tour="wordpress"]',
      title: 'WordPress Plugin',
      content: 'Integrate your membership plans with your WordPress website. Generate API keys and download the plugin.',
      position: 'right'
    }
  ];

  const patientSteps = [
    {
      target: '[data-tour="dashboard"]',
      title: 'Welcome! 👋',
      content: 'This is your personal dashboard. View your membership status, upcoming appointments, and quick actions.',
      position: 'bottom'
    },
    {
      target: '[data-tour="membership-card"]',
      title: 'Your Membership',
      content: 'Check your current membership plan, expiration date, and benefits.',
      position: 'bottom'
    },
    {
      target: '[data-tour="appointments"]',
      title: 'Appointments',
      content: 'View your upcoming appointments and schedule new ones.',
      position: 'bottom'
    },
    {
      target: '[data-tour="profile"]',
      title: 'Profile Settings',
      content: 'Update your personal information, contact details, and preferences.',
      position: 'left'
    }
  ];

  const steps = role === 'admin' ? adminSteps : patientSteps;
  const step = steps[currentStep];

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      completeTour();
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSkip = () => {
    completeTour();
  };

  const completeTour = useCallback(async () => {
    // Save to localStorage immediately for instant UI response
    localStorage.setItem(`tour_completed_${role}`, 'true');
    setIsVisible(false);
    
    // Also save to backend for persistence
    try {
      await axios.post(`${API}/auth/user/complete-onboarding`);
    } catch (error) {
      console.error('Failed to save onboarding status to server:', error);
      // Still keep it hidden since localStorage has it
    }
    
    if (onComplete) onComplete();
  }, [role, onComplete]);

  // Handle clicking outside the modal to close it
  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      completeTour();
    }
  };

  // Handle Escape key to close
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isVisible) {
        completeTour();
      }
    };
    
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isVisible, completeTour]);

  if (!isVisible || !step) return null;

  return (
    <>
      {/* Overlay - clicking on it closes the tour */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 z-50 cursor-pointer" 
        onClick={handleOverlayClick}
      />

      {/* Tour Popup */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
        <Card className="w-full max-w-md pointer-events-auto shadow-2xl">
          <CardContent className="p-6">
            {/* Header */}
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900">{step.title}</h3>
                <p className="text-sm text-gray-600 mt-2">{step.content}</p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleSkip}
                className="ml-2"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>

            {/* Progress */}
            <div className="mb-4">
              <div className="flex items-center justify-between text-sm text-gray-600 mb-2">
                <span>Step {currentStep + 1} of {steps.length}</span>
                <span>{Math.round(((currentStep + 1) / steps.length) * 100)}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-teal-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${((currentStep + 1) / steps.length) * 100}%` }}
                />
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center justify-between gap-3">
              <Button
                variant="outline"
                size="sm"
                onClick={handleSkip}
              >
                Skip Tour
              </Button>

              <div className="flex gap-2">
                {currentStep > 0 && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handlePrevious}
                  >
                    <ArrowLeft className="h-4 w-4 mr-1" />
                    Previous
                  </Button>
                )}
                <Button
                  size="sm"
                  onClick={handleNext}
                  className="bg-teal-600 hover:bg-teal-700"
                >
                  {currentStep === steps.length - 1 ? (
                    <>
                      <Check className="h-4 w-4 mr-1" />
                      Finish
                    </>
                  ) : (
                    <>
                      Next
                      <ArrowRight className="h-4 w-4 ml-1" />
                    </>
                  )}
                </Button>
              </div>
            </div>
            
            {/* Help text */}
            <p className="text-xs text-gray-400 mt-4 text-center">
              Press Escape or click outside to dismiss
            </p>
          </CardContent>
        </Card>
      </div>
    </>
  );
};

export default OnboardingTour;
